import React, { ReactNode, useEffect } from "react";
import PageAreaHolder from "./PageAreaHolder";
import MosaicPageViewer from "./MosaicPageViewer";
import styled from "styled-components";
import { connect } from "react-redux";
import { createPageViewer } from "../api/features/viewer/actions";
import GlobalStyleProvider from "../../../../common/providers/GlobalStyleProvider";
import { useBrand } from "../../../../common/providers/brand/brand-context";
import BrandedPageTitleProvider from "../../../../common/providers/BrandedPageTitleProvider";
import useBookInfo from "../connectors/bookinfo/useBookInfo";
import usePageBreaks from "../connectors/nav/usePageBreaks";
import {
  combineDetailedStatuses,
  DetailedLoadStatus,
} from "../../../../common/utils/async-status";
import { Redirect } from "react-router-dom";
import { ERROR_SYSTEM } from "../../../../main/route";
import useUniversalUrl from "../connectors/nav/useUniversalUrl";
import useRouteSync from "../../../../common/ui/useRouteSync";
import useApplySettings from "../connectors/content-controls/enhanced/useApplySettings";
import useUser from "../connectors/user/useUser";
import PageErrors from "./PageErrors";
import usePageError from "../connectors/nav/usePageError";
import usePageLoading from "../connectors/nav/usePageLoading";
import useKeyboardShortcuts from "../connectors/nav/useKeyboardShortcuts";
import useApplyTTSSettings from "../connectors/tts/useApplyTTSSettings";
import useExternalReaderAPI from "./useExternalReaderAPI";
import useIsSnippet from "../connectors/viewer/useIsSnippet";
import ErrorToast from "./ErrorToast";
import RedirectToLogin from "../../../../common/ui/RedirectToLogin";
import useSpine from "../connectors/spine/useSpine";
import PageLoadAnnounce from "./PageLoadAnnounce";
import useInitialQueryParams from "../../../../common/ui/useInitialQueryParams";
import { useIntl } from "react-intl";
import useStaticPadding from "../connectors/padding/useStaticPadding";
import { Kind } from "../api/features/bookinfo/types";
import RedirectToBookshelf from "../../../../common/ui/RedirectToBookshelf";

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;

interface PageViewFrameworkProps {
  children: ReactNode;
  vbid: string;
  createViewer: (opts) => void;
  location?: string;
}

function PageViewFramework({
  children,
  vbid,
  createViewer,
  location,
}: PageViewFrameworkProps) {
  const intl = useIntl();
  const brand = useBrand();
  const brandName = brand.getProp<string>("brand");
  const staticPadding = useStaticPadding();
  const { bookInfo, loadStatus: bookInfoLoadStatus } = useBookInfo();
  const { loadStatus: pageBreakLoadStatus } = usePageBreaks();
  const { loadStatus: userLoadStatus, isLoggedOut } = useUser();
  const { loadStatus: spineLoadStatus } = useSpine();
  const loadStatuses = combineDetailedStatuses([
    bookInfoLoadStatus,
    pageBreakLoadStatus,
    userLoadStatus,
    spineLoadStatus,
  ]);

  const isSnippet = useIsSnippet();
  const { urlSuffix } = useUniversalUrl();
  useRouteSync(isSnippet ? undefined : urlSuffix);

  useApplySettings();
  useApplyTTSSettings();
  useKeyboardShortcuts();
  const pageLoading = usePageLoading();
  const pageError = usePageError();
  useExternalReaderAPI();
  const initialQueryParams = useInitialQueryParams();
  const isSubscription = bookInfo && bookInfo.kind === Kind.SUBSCRIPTION;

  useEffect(() => {
    if (!brand.getProp("NativeContextMenu.enabled")) {
      const handler = (e) => e.preventDefault();
      window.addEventListener("contextmenu", handler);
      return () => window.removeEventListener("contextmenu", handler);
    }
  }, [brand]);

  function renderChildren() {
    if (isSubscription) {
      return <RedirectToBookshelf path={`/explore/${vbid}`} replace />;
    } else if (isLoggedOut) {
      return <RedirectToLogin finalizeLogout={true} />;
    } else if (loadStatuses === DetailedLoadStatus.OK) {
      return children;
    } else if (
      loadStatuses === DetailedLoadStatus.ERROR &&
      !pageLoading &&
      !pageError
    ) {
      // These endpoints may error before we hear about the page error, and the
      // page error should take priority since some page errors will also lead
      // to a related endpoint error.
      // Non-401 user errors will also send you here
      // Logged via middleware
      return <Redirect to={ERROR_SYSTEM} />;
    } else {
      return null;
    }
  }

  return (
    <Container>
      <BrandedPageTitleProvider
        brandName={brandName}
        detail={bookInfo?.title}
      />
      <GlobalStyleProvider styles="body { overflow: hidden; }" />
      {renderChildren()}
      <PageAreaHolder>
        <MosaicPageViewer
          vbid={vbid}
          location={location === "recent" ? "pages/recent" : location}
          initialQueryParams={initialQueryParams}
          title={intl.formatMessage({ id: "reader.title" })}
          staticPadding={staticPadding}
          createPageViewer={createViewer}
        />
      </PageAreaHolder>
      <PageLoadAnnounce />
      {/* Inline, so it will overlay the reader if it occurs on future page load */}
      <PageErrors />
      <ErrorToast />
    </Container>
  );
}

export default connect(null, (dispatch: any) => ({
  createViewer: (opts) => dispatch(createPageViewer(opts)),
}))(PageViewFramework);
